import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const DOC_TYPE_LABELS = {
  SoleAuth: 'Sole Authorization',
  ProofOccupancy: 'Residency Affidavit',
  ProofLLC: 'Trust/LLC docs',
  CommAuth: 'Commercial Authorization',
};

const CLOSED_STATUSES = new Set(['completed', 'complete', 'cancelled', 'canceled', 'closed', 'withdrawn']);
const COMPLETED_STATUSES = new Set(['completed', 'complete', 'closed']);
const CANCELLED_STATUSES = new Set(['cancelled', 'canceled', 'withdrawn']);

const formatEstDate = (value) => {
  const raw = String(value).trim();
  const zoned = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
  const naive = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (naive && !zoned) {
    const [, year, month, day] = naive;
    return `${month}/${day}/${year.slice(-2)}`;
  }
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  }).format(date);
};

const requestStatus = (req) => String(req?.status || '').toLowerCase();

export const completedDocRequests = (requests) => (
  Array.isArray(requests)
    ? requests.filter((req) => req && !CANCELLED_STATUSES.has(requestStatus(req)) && (req.dateCompleted || COMPLETED_STATUSES.has(requestStatus(req))))
    : []
);

export const docRequestStatus = (requests) => {
  const open = Array.isArray(requests)
    ? requests.filter((req) => req && !req.dateCompleted && !CLOSED_STATUSES.has(requestStatus(req)))
    : [];
  if (!open.length) return null;
  const types = [...new Set(open.map((req) => DOC_TYPE_LABELS[req.docType] || req.docType || 'Document'))];
  return {
    open,
    statusDisplay: open.length === 1 ? 'Doc Request' : `Doc Requests (${open.length})`,
    statusTooltip: `Open document request${open.length === 1 ? '' : 's'}: ${types.join(', ')}`,
  };
};

const requestDetailLines = (req) => {
  const completed = req.dateCompleted || COMPLETED_STATUSES.has(requestStatus(req));
  const lines = [`${DOC_TYPE_LABELS[req.docType] || req.docType || 'Document'}${completed ? ' (Completed)' : ''}`];
  if (req.requestedFrom?.name || req.requestedFrom?.email) {
    lines.push(`${req.requestedFrom.name || ''}${req.requestedFrom.email ? ` (${req.requestedFrom.email})` : ''}`.trim());
  }
  if (req.dateRequested || req.requestedBy) {
    lines.push(`${req.dateRequested ? `Requested ${formatEstDate(req.dateRequested)}` : 'Requested'}${req.requestedBy ? ` by ${req.requestedBy}` : ''}`);
  }
  if (req.dateCompleted) lines.push(`Completed ${formatEstDate(req.dateCompleted)}`);
  if (!completed && req.reminders?.paused) lines.push('Reminders paused');
  return lines;
};

export const openDocRequestTooltipText = (requests) => {
  const status = docRequestStatus(requests);
  if (!status) return '';
  const header = status.open.length === 1 ? 'Document request:' : 'Document requests:';
  return `${header}\n${status.open.map((req) => requestDetailLines(req).join('\n')).join('\n\n')}`;
};

const RequestDetails = ({ req, number }) => {
  const lines = requestDetailLines(req);
  if (number != null) lines[0] = `${number}. ${lines[0]}`;
  return (
    <div>
      {lines.map((line, index) => <div key={index}>{line}</div>)}
    </div>
  );
};

const chipClass = (compact, muted) => compact
  ? `px-2 py-0.5 ${muted ? 'bg-gray-100 text-gray-500' : 'bg-violet-100 text-violet-900'} rounded text-xs font-semibold leading-tight whitespace-nowrap`
  : `px-3 py-1 ${muted ? 'bg-gray-100 text-gray-500' : 'bg-violet-100 text-violet-900'} rounded-md text-sm font-medium mr-2 whitespace-nowrap`;

const DocRequestChip = ({ requests, compact = false, label }) => {
  const status = docRequestStatus(requests);
  if (!status) return null;
  const chipLabel = label ? status.statusDisplay.replace(/^Doc Request/, label) : status.statusDisplay;

  return (
    <Tooltip
      title={(
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {status.open.map((req, index) => (
            <RequestDetails key={req.requestId ?? index} req={req} />
          ))}
        </div>
      )}
      placement="top"
      disableInteractive
    >
      <div className={chipClass(compact)}>
        {chipLabel}
      </div>
    </Tooltip>
  );
};

export const CompletedDocRequestChip = ({ requests, compact = false }) => {
  const completed = completedDocRequests(requests);
  if (!completed.length) return null;

  return (
    <Tooltip
      title={(
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {completed.map((req, index) => (
            <RequestDetails key={req.requestId ?? index} req={req} number={index + 1} />
          ))}
        </div>
      )}
      placement="top"
      disableInteractive
    >
      <div className={`${chipClass(compact, true)} inline-flex items-center gap-1`}>
        Completed Doc Requests
        <InfoOutlinedIcon sx={{ fontSize: compact ? 12 : 14 }} />
      </div>
    </Tooltip>
  );
};

export default DocRequestChip;
